/* (c) Dalineage, s.r.o. 2020-2025, all rights reserved */
package com.dalineage.client

import org.scalajs.dom
import dom.html
/*
import org.scalajs.dom.{document, html, MouseEvent}
import scala.scalajs.js

import com.dalineage.common
*/


object Panels {

  var separators: List[html.Div] = scala.compiletime.uninitialized
  var panels: List[html.Div] = scala.compiletime.uninitialized
  var container: html.Div = scala.compiletime.uninitialized
  var helpDiv: html.Div = scala.compiletime.uninitialized

  val minPanelWidth = 50 // pixels

  val panelColors = Seq("#f0f0f0", "#e0e0e0", "#F0F0F0", "#c0c0c0", "#ffffff")

  // Create Panels and Separators
  def init(container: html.Div): List[html.Div] = {

    this.container = container

    val helpHeight = 40
    container.style.height = s"calc(100vh - ${helpHeight}px)"

    panels = panelColors.zipWithIndex.map { case (color, index) =>
      val panel = dom.document.createElement("div").asInstanceOf[html.Div]
      panel.className = "panel"
      panel.style.width = s"${100.0 / panelColors.length}%"
      panel.style.backgroundColor = color
      panel.style.outline = "none"
      panel.tabIndex = index
      panel
    }.toList

    separators = (1 until panels.length).map { _ =>
      val separator = dom.document.createElement("div").asInstanceOf[html.Div]
      separator.className = "separator"
      separator
    }.toList

    // Append Panels and Separators to Container
    for (i <- panels.indices) {
      container.appendChild(panels(i))
      if (i < separators.length) {
        container.appendChild(separators(i))
      }
    }

    // Handle Dragging
    var isDragging = false
    var currentSeparatorIndex = -1


    separators.zipWithIndex.foreach { case (separator, index) =>
      separator.onmousedown = (e: dom.MouseEvent) => {
        isDragging = true
        currentSeparatorIndex = index
        e.preventDefault()
      }
    }

    helpDiv = dom.document.getElementById("help").asInstanceOf[html.Div]
    helpDiv.id = "helpDiv"
    helpDiv.tabIndex = 15
    helpDiv.style.border = "1px solid black"
    helpDiv.style.backgroundColor = "lightgray"
    helpDiv.style.textAlign = "center"
    helpDiv.style.verticalAlign = "middle"
    helpDiv.style.width = "100%"
    helpDiv.style.height = s"${helpHeight}px"
    helpDiv.style.lineHeight = s"${helpHeight}px"

    dom.window.onmousemove = (e: dom.MouseEvent) => {
      if (isDragging && currentSeparatorIndex >= 0) {
        val containerRect = container.getBoundingClientRect()
        val totalWidth = containerRect.width
        val mouseX = e.clientX - containerRect.left

        // Calculate cumulative widths up to the left panel
        val leftPanelsWidth = (0 until currentSeparatorIndex).map(panels(_).offsetWidth).sum
        val leftSeparatorsWidth = (0 until currentSeparatorIndex).map(separators(_).offsetWidth).sum

        val leftOffset = leftPanelsWidth + leftSeparatorsWidth

        // Calculate new widths
        val newLeftPanelWidth = mouseX - leftOffset
        val rightPanelWidth = panels(currentSeparatorIndex + 1).offsetWidth
        val delta = newLeftPanelWidth - panels(currentSeparatorIndex).offsetWidth
        val newRightPanelWidth = rightPanelWidth - delta

        // Enforce minimum widths
        if (newLeftPanelWidth >= minPanelWidth && newRightPanelWidth >= minPanelWidth) {
          val totalPanelsWidth = panels.map(_.offsetWidth).sum + separators.map(_.offsetWidth).sum

          panels(currentSeparatorIndex).style.width = s"${(newLeftPanelWidth / totalPanelsWidth) * 100}%"
          panels(currentSeparatorIndex + 1).style.width = s"${(newRightPanelWidth / totalPanelsWidth) * 100}%"
        }
      }
    }

    dom.window.onmouseup = (_: dom.MouseEvent) => {
      isDragging = false
      currentSeparatorIndex = -1
    }
    panels
  }

  // Function to shrink the i-th panel to minimum width
  def shrinkPanelToMinimum(i: Int): Unit = {
    if (i >= 0 && i < panels.length) {
      val containerWidth = container.getBoundingClientRect().width
      val totalSeparatorWidth = separators.map(_.offsetWidth).sum

      // Set the i-th panel's width to minimum
      panels(i).style.width = panels(i).offsetWidth match
        case width if width < minPanelWidth + 8 //magic number
          => s"${100.0 / panelColors.length}%"
        case _ => s"${(minPanelWidth / containerWidth) * 100}%"

      // Distribute the remaining width among other panels proportionally
      val remainingWidth = containerWidth - panels(i).offsetWidth - totalSeparatorWidth
      val otherPanels = panels.indices.filter{ index =>
        index != i && panels(index).offsetWidth > minPanelWidth + 8//magic number
      }
      val totalCurrentWidth = otherPanels.map(panels(_).offsetWidth).sum

      otherPanels.foreach { idx =>
        val currentWidth = panels(idx).offsetWidth
        val newWidth = (currentWidth / totalCurrentWidth) * remainingWidth
        // Enforce minimum width
        val adjustedWidth = Math.max(newWidth, minPanelWidth)
        panels(idx).style.width = s"${(adjustedWidth / containerWidth) * 100}%"
      }
    }
  }
}
